import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

/**
 * AdSlot — fetches and renders active paid commercial ads for a placement.
 * Tracks impressions once per ad, and clicks on the CTA.
 *
 * Props:
 *   placement: "homepage_top" | "homepage_mid" | "homepage_bottom" | "sidebar" | "search_results" | "listing_detail"
 *   variant: "hero" | "banner" | "card" (visual style)
 *   limit: max ads to show (default 1)
 */
export default function AdSlot({ placement, variant = "banner", limit = 1 }) {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const seen = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/admin/public/ads", { params: { placement, limit } })
      .then(({ data }) => { if (!cancelled) setAds(data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [placement, limit]);

  // Fire impression once per ad rendered
  useEffect(() => {
    ads.forEach(a => {
      if (!seen.current.has(a.id)) {
        seen.current.add(a.id);
        apiClient.post(`/admin/public/ads/${a.id}/impression`).catch(() => {});
      }
    });
  }, [ads]);

  if (ads.length === 0) return null;

  const onClick = (ad, e) => {
    apiClient.post(`/admin/public/ads/${ad.id}/click`).catch(() => {});
    // iter306 — Route rule for ad CTAs:
    //   • Internal relative paths (starting with "/") must NOT open in a
    //     new tab and must NOT trigger a full reload. Use in-app
    //     navigation instead. This kills the "loops back to the same
    //     page" symptom that <a target="_blank" href="/…"> causes when
    //     the browser interprets the click ambiguously.
    //   • For logged-out visitors, any ad CTA that would land on the
    //     individual-only /register track is redirected to the unified
    //     /join chooser (personal vs. CARTAL Business). Logged-in
    //     users keep the destination the admin configured.
    const link = ad?.link || "";
    if (typeof link === "string" && link.startsWith("/")) {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      let target = link;
      const isRegisterTrack =
        target === "/register" ||
        target.startsWith("/register?") ||
        target.startsWith("/register/") ||
        target === "/signup" ||
        target.startsWith("/signup?");
      if (!user && isRegisterTrack) target = "/join";
      navigate(target);
    }
  };

  if (variant === "hero") {
    return (
      <div className="my-8" data-testid={`adslot-${placement}`}>
        {ads.map(ad => (
          <a
            key={ad.id}
            href={ad.link}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={(e) => onClick(ad, e)}
            className="block relative overflow-hidden rounded-2xl group"
            style={{ minHeight: 280 }}
            data-testid={`ad-${ad.id}`}
          >
            <img src={ad.image} alt={ad.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-[#0a0a0a]/40 to-transparent" />
            <div className="absolute top-3 end-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#0a0a0a]/70 text-[#f1ecdf]">
              {lang === "ar" ? "إعلان" : "Sponsored"}
            </div>
            <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 text-[#f1ecdf]">
              {ad.advertiser_logo && <img src={ad.advertiser_logo} alt="" className="h-7 mb-3 bg-white/90 rounded p-1" />}
              <h3 className="font-display text-2xl sm:text-3xl mb-1">{ad.title}</h3>
              {ad.subtitle && <p className="text-sm opacity-90 mb-4">{ad.subtitle}</p>}
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a55c] text-[#0a0a0a] text-sm font-medium">
                {ad.cta_label || (lang === "ar" ? "اعرف المزيد" : "Learn more")} →
              </span>
            </div>
          </a>
        ))}
      </div>
    );
  }

  // Default banner variant
  return (
    <div className="my-6 space-y-3" data-testid={`adslot-${placement}`}>
      {ads.map(ad => (
        <a
          key={ad.id}
          href={ad.link}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={(e) => onClick(ad, e)}
          className="block surface rounded-xl overflow-hidden hover:shadow-md transition-shadow"
          data-testid={`ad-${ad.id}`}
        >
          <div className="flex items-center gap-4 p-4 sm:p-5">
            {ad.image && <img src={ad.image} alt="" className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {ad.advertiser_logo && <img src={ad.advertiser_logo} alt="" className="h-5 object-contain" />}
                <span className="text-[10px] uppercase tracking-widest text-[#8a6f2e]">
                  {lang === "ar" ? "إعلان مدفوع" : "Sponsored"}
                </span>
              </div>
              <h4 className="font-display text-lg text-[#0a0a0a] truncate">{ad.title}</h4>
              {ad.subtitle && <p className="text-sm text-[#6b6b6b] truncate">{ad.subtitle}</p>}
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#8a6f2e] font-medium">
                {ad.cta_label || (lang === "ar" ? "اعرف المزيد" : "Learn more")} →
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}
